import _ from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { useContext, useMemo, useState } from 'react';
import { Form, FormGroup } from 'react-bootstrap';
import { Input, Modal, ModalBody, ModalHeader } from 'reactstrap';
import PDFViewer from '../../../../../components/Image/PDF';
import { setValue } from '../../../../../components/InputField/helper';
import PreviousValue from '../../../../../components/InputField/PreviousValue';
import { DialogContext } from '../../../../../store/context/DialogContext';
import { useTranslation } from '../../../../../store/context/TranslationContext';
import ButtonGroupNew from '../../BusinessLiteForms/components/ButtonGroupNew';
import { getVisibleWizardFieldOrder, WIZARD_NUM } from '../helper';
import { buildAutomationChildTestId, buildAutomationValueTestId, resolveAutomationTestId } from '../../../../../components/InputField/testId';

export const QuestionRadioGroupMultiple = ({ isDisabled, field, question, data, handleChange, hasError, style, options, required, showErr, error, prevValue }) => {
    const valueToLabelMap = useMemo(() => Object.fromEntries(options?.map(({ value, label }) => [value, label]) || []), [options]);
    const { t } = useTranslation();
    const fieldTestId = resolveAutomationTestId(field);
    
    return (
        <div className='fv__selection-field fv__selection-field--radio mt-1 mb-2' data-testid={fieldTestId}>
            <FormGroup className='radio-multiple'>
                <Form.Label className='mt-3' style={{ minWidth: "120px", padding: "0px", fontSize: 16, fontWeight: "500", ...style }}>
                    {t(question)}
                    {!!required ? <font color="red">*</font> : ""}
                </Form.Label>
                <div className='row'>
                    {options?.map((e) => {
                        const optionTestId = buildAutomationValueTestId(fieldTestId, e?.value ?? e?.label);
                        return (
                        <div key={e?.value ?? e?.label} className='col-12 col-md-6 mb-2'>
                            <Form.Check
                                id={optionTestId}
                                data-testid={optionTestId}
                                type="radio"
                                disabled={isDisabled}
                                label={t(e.label)}
                                name={field}
                                value={e.value}
                                checked={_.get(data, field) === e.value}
                                onChange={() => handleChange(field, e.value)}
                                className={`me-2 ${prevValue?.has ? "is-changed" : ""} ${isDisabled ? "is-disabled" : ""}`}
                            />
                        </div>
                        );
                    })}
                </div>
                <PreviousValue value={valueToLabelMap[prevValue?.value] ?? prevValue?.value} show={prevValue?.has} testId={buildAutomationChildTestId(fieldTestId, "previous")} />
                {data?.[field] === undefined && (showErr || hasError) && (
                    <div className="ps-0 ms-0 w-100 error__message" data-testid={buildAutomationChildTestId(fieldTestId, "error")}>{error}</div>
                )}
                {!isDisabled && error && showErr ? (
                    <em className="inputfield__error" data-testid={buildAutomationChildTestId(fieldTestId, "validation-error")}>
                        {error ? t(error) : "Please enter Something"}
                    </em>
                ) : null}
            </FormGroup>
        </div>
    );
};

const ReferralAndSource = ({
    asLabel,
    hideButtons,
    data,
    handleUpdate,
    hasError,
    setHasError,
    loading,
    cyclosFields,
    errors,
    setErrors,
    iPlusFields,
    wizard,
    isForEDD,
    handleChange,
    handlePrev,
    setData,
    editId,
    applicationForm,
    editMode,
}) => {
    const { showError } = useContext(DialogContext);
    const { t } = useTranslation();
    const [modal, setModal] = useState(false);
    const toggleModal = () => setModal(!modal);
    const tncTestId = `${wizard || 'ReferralAndSource'}-tnc`;

    const checkNext = () => {
        const validationErrors = [];

        // Check required fields
        if (!data?.tnc) {
            validationErrors.push("Please agree to the Certificate of Beneficial Owners");
        }

        const visibleFieldOrder = getVisibleWizardFieldOrder({
            fields: iPlusFields,
            wizard,
            data,
        });

        for (const fieldPath of visibleFieldOrder) {
            const fieldError = _.get(errors, fieldPath);
            if (fieldError) {
                validationErrors.push(fieldError);
            }
        }

        if (validationErrors.length > 0) {
            setHasError(true);
            showError(validationErrors[0]);
            return false;
        }
        
        setErrors({});
        return true;
    };

    const handleSubmit = () => {
        const isFormFieldValid = checkNext();
        if (isFormFieldValid) {
            setHasError(false);
            const nextStep = applicationForm?.getNextStepForForm?.({
                wizard,
                applicationForm,
                data,
                editMode,
            });
            if (nextStep) {
                return handleUpdate({ nextStep });
            }
            // This is typically the final step, so we submit with last step flag
            return handleUpdate({ isLastStep: true });
        }
    };

    const handleUpdateFormData = (updatedData) => {
        handleChange(updatedData);
    };

    const handleErrors = (field, errorMsg) => {
        setErrors((prevErrors) => {
            const updatedErrors = _.cloneDeep(prevErrors);
            updatedErrors[field] = errorMsg;
            return updatedErrors;
        });
    };

    const handleFieldChange = (field, value) => {
        let updatedData = { ...data };
        updatedData = setValue(updatedData, field, value, isForEDD);
        handleChange(updatedData);
    };

    return (
        <div className="row">
            <div className="col-12">
                <h2 style={{ fontWeight: 'bolder' }} className='pt-3 mb-4'>Referral and Source</h2>
                
                <div className='mb-3'>
                    <div className='d-flex mt-4 align-items-center'>
                        <Input
                            id={tncTestId}
                            data-testid={tncTestId}
                            type="checkbox"
                            checked={data?.tnc}
                            onChange={(e) => {
                                if (!asLabel) {
                                    handleFieldChange('tnc', e.target.checked);
                                }
                            }}
                        />
                        <span className='ps-2'>
                            I agree to the information provided in the
                            <span 
                                style={{ cursor: "pointer" }} 
                                onClick={() => { setModal(true) }} 
                                className='text-primary'
                            > 
                                Certificate of Beneficial Owners
                            </span>,
                            is accurate and complete to the best of my knowledge.
                        </span>
                    </div>

                    <Modal size="xl" isOpen={modal} toggle={toggleModal} centered>
                        <ModalHeader toggle={toggleModal}>Certificate of Beneficial Owners</ModalHeader>
                        <ModalBody className="fv_doc_field_modal_body">
                            <PDFViewer
                                modalLoader
                                pdfStyle={{ height: "calc(80vh - 40px)" }}
                                src={`/api/application-doc/cert/beneficial-owners?form_id=${editId}`} 
                                alt="Document" 
                            />
                        </ModalBody>
                    </Modal>
                </div>

                <div className='mt-4'>
                    <ButtonGroupNew
                        onNextClick={handleSubmit}
                        onBackClick={() => { handlePrev() }}
                        step={WIZARD_NUM.ReferralAndSource}
                        showPageNum={false}
                        pageNum={WIZARD_NUM.ReferralAndSource}
                        hideSubmitButton={hideButtons}
                        asLabel={false}
                        loading={loading}
                        nextLabel="Submit"
                    />
                </div>
            </div>
        </div>
    );
};

export default observer(ReferralAndSource);
